import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, DatePicker, Alert, Table, Typography, Tag, Space, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SupplierQuote } from '../types.ts';
import { supplierQuoteAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';

const { TextArea } = Input;
const { Title, Text } = Typography;

// Conversione preventivo fornitore → ordine fornitore (stato "In Attesa"). Stesso
// schema a passi di "Trasforma in Ordine Fornitore": conferma → elaborazione → risultato.
// Se il preventivo è collegato a un ordine cliente Confermato, l'ordine cliente passa
// a In Lavorazione (come nella trasformazione da ordine cliente).
interface ConvertSupplierQuoteModalProps {
  visible: boolean;
  quote: SupplierQuote | null;
  onClose: () => void;
  onConverted?: () => void;
}

const ConvertSupplierQuoteModal: React.FC<ConvertSupplierQuoteModalProps> = ({ visible, quote, onClose, onConverted }) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState<'confirm' | 'processing' | 'result'>('confirm');
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!quote || !visible) return;
    setStep('confirm');
    setResult(null);
    const days = quote.delivery_time_days || 0;
    form.setFieldsValue({
      order_number: `ORD-FORN-${quote.quote_number}`.toUpperCase(),
      order_date: dayjs(),
      delivery_date: days > 0 ? dayjs().add(days, 'day') : null,
      notes: undefined,
    });
  }, [quote, visible, form]);

  const handleConvert = async () => {
    if (!quote) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setStep('processing');
    try {
      const response = await supplierQuoteAPI.convertToOrder(quote.id, {
        order_number: values.order_number,
        order_date: values.order_date ? values.order_date.format('YYYY-MM-DD') : undefined,
        delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null,
        notes: values.notes || null,
      });
      setResult(response);
      setStep('result');
      message.success(response?.message || 'Preventivo convertito in ordine fornitore');
      onConverted?.();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nella conversione del preventivo');
      setStep('confirm');
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setResult(null);
    onClose();
  };

  if (!quote) return null;
  const details = quote.quote_details || [];
  const linkedOrder = quote.customer_order;

  const rowColumns = [
    { title: 'Prodotto', key: 'product', ellipsis: true,
      render: (_: any, r: any) => r.product ? <Text code title={formatProductDisplay(r.product)}>{formatProductDisplay(r.product)}</Text> : `ID ${r.product_id}` },
    { title: 'Qta', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'center' as const },
    { title: 'Costo Unit.', dataIndex: 'unit_cost', key: 'unit_cost', width: 110, align: 'right' as const, render: (v: number) => `€ ${formatCurrency(v || 0)}` },
    { title: 'Totale', dataIndex: 'total_cost', key: 'total_cost', width: 110, align: 'right' as const, render: (v: number) => `€ ${formatCurrency(v || 0)}` },
  ];

  const renderConfirm = () => (
    <div>
      <Alert
        message={`Conversione del preventivo fornitore ${quote.quote_number}`}
        description={
          <div>
            <div>Fornitore: <strong>{quote.supplier?.name || 'N/A'}</strong> — Importo: <strong>€ {formatCurrency(quote.total_amount || 0)}</strong></div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>L'ordine fornitore nasce in stato <Tag color="gold">In Attesa</Tag> con le righe del preventivo (costi e note comprese); i costi aggiornano il listino costi dei prodotti.</li>
              {linkedOrder ? (
                <li>
                  Resta collegato all'ordine cliente <strong>{linkedOrder.order_number}</strong>
                  {linkedOrder.order_status_id === 3
                    ? <> che, essendo <Tag color="green">Confermato</Tag>, passa a <Tag color="orange">In Lavorazione</Tag>.</>
                    : ' (il suo stato non cambia).'}
                </li>
              ) : (
                <li>Nessun ordine cliente collegato: l'ordine fornitore nasce autonomo.</li>
              )}
              <li>Il preventivo passa a <Tag color="purple">Evaso</Tag> e non sarà più modificabile.</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="order_number" label="Numero Ordine Fornitore" rules={[{ required: true, message: 'Inserire il numero ordine' }, { max: 100, message: 'Massimo 100 caratteri' }]}>
            <Input
              placeholder="Numero ordine fornitore"
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => form.setFieldValue('order_number', e.target.value.toUpperCase())}
            />
          </Form.Item>
          <Form.Item name="order_date" label="Data Ordine" rules={[{ required: true, message: 'Selezionare la data ordine' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="delivery_date" label="Data Consegna Fornitore" extra={quote.delivery_time_days ? `Proposta: oggi + ${quote.delivery_time_days} giorni` : undefined}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Note Ordine (opzionali)">
          <TextArea rows={2} placeholder="Note aggiuntive per l'ordine fornitore" />
        </Form.Item>
      </Form>

      <Title level={5}>Righe che verranno riportate nell'ordine</Title>
      <Table
        columns={rowColumns}
        dataSource={details}
        rowKey={(r: any) => r.id ?? r.product_id}
        size="small"
        pagination={false}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}><Text strong>Totale</Text></Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Text strong>€ {formatCurrency(details.reduce((s, d) => s + (d.total_cost || 0), 0))}</Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );

  const renderProcessing = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <ShoppingCartOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
      <Title level={4}>Conversione in corso...</Title>
      <Text>Sto creando l'ordine fornitore dal preventivo</Text>
    </div>
  );

  const renderResult = () => (
    <div style={{ textAlign: 'center' }}>
      <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
      <Title level={4}>Conversione Completata</Title>
      <Text>{result?.message}</Text>
      {result?.supplier_order && (
        <div style={{ marginTop: 16 }}>
          <Space direction="vertical">
            <Text>Ordine fornitore: <Tag color="green" style={{ fontSize: 14 }}>{result.supplier_order.order_number}</Tag></Text>
            <Text>Fornitore: <strong>{result.supplier_order.supplier_name}</strong> — Stato: <Tag color="gold">{result.supplier_order.order_status || 'In Attesa'}</Tag></Text>
            {result.supplier_order.delivery_date && <Text>Consegna prevista: <strong>{new Date(result.supplier_order.delivery_date).toLocaleDateString()}</strong></Text>}
            {result.customer_order && (
              <Text>
                Ordine cliente {result.customer_order.order_number}:{' '}
                {result.customer_order_status_updated
                  ? <Tag color="orange">{result.customer_order.new_status}</Tag>
                  : <Text type="secondary">stato invariato</Text>}
              </Text>
            )}
            <Text type="secondary">L'ordine è disponibile in Ordini → Ordini Fornitori.</Text>
          </Space>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} />Converti Preventivo in Ordine Fornitore</Space>}
      open={visible}
      onCancel={handleClose}
      width={step === 'confirm' ? 900 : 600}
      destroyOnClose
      footer={
        step === 'confirm' ? [
          <Button key="cancel" onClick={handleClose}>Annulla</Button>,
          <Button key="convert" type="primary" onClick={handleConvert}>Converti in Ordine Fornitore</Button>,
        ] : step === 'processing' ? null : [
          <Button key="close" type="primary" onClick={handleClose}>Chiudi</Button>,
        ]
      }
    >
      {step === 'confirm' && renderConfirm()}
      {step === 'processing' && renderProcessing()}
      {step === 'result' && renderResult()}
    </Modal>
  );
};

export default ConvertSupplierQuoteModal;
